/**
 * DrPanel — the shared Data-Request slide-over (Wave 3 parcel L1, audit gap G2:
 * "DR references currently render as inert text everywhere; the engineer's trail
 * goes cold"). Every DR mention in the app (`DrChip`, see primitives.tsx) opens
 * THIS panel instead of dead-ending on plain text.
 *
 * Two ways to mount it — document both, L2 (lead views / register / gate) wires
 * the second:
 *
 *   1. Deep link — any `/interlock/*` route: append `?dr=DR-01` to the URL.
 *      `InterlockScaffold` (the shared page shell every Interlock page renders
 *      through) watches that search param and mounts/unmounts this panel, so a
 *      link into `?dr=DR-13` from the register, gate, or home page opens the
 *      panel on top of whatever page it lands on. Contract: read/write the `dr`
 *      param, nothing else — leave other params on the URL untouched.
 *
 *   2. Direct component — `<DrPanel refId="DR-01" onClose={() => ...} />` for a
 *      caller that wants to own the open/close state itself rather than going
 *      through the URL (e.g. a modal-in-modal case). Fully self-contained: it
 *      does its own fetching, renders its own scrim, and calls `onClose` on
 *      scrim-click / ✕ / Escape.
 *
 * Data sources (no invented fields — see interlockApi.ts):
 *   - `fetchDataRequests()` → GET /api/projects/:id/data-requests (the DR row
 *     itself: ask, status, answerSummary, answeredAt, createdAt).
 *   - `fetchDrGating(refId)` → batches GET /api/interfaces/:id across the
 *     project and keeps the ones whose real `links.dataRequests` includes this
 *     DR, deriving "what it gates" (interfaces + their open trades) from data
 *     the backend already returns — there is no dedicated reverse-lookup route.
 *   - "log a chase note" → POST /api/data-requests/:id/chase (V8 — R2 W3-A).
 *     The composer is ALWAYS available (not gated on there being an open trade
 *     to echo into); the backend appends `{byUserId, at, note}` to
 *     `DataRequest.chaseLog` AND — in the same call — echoes the note into every
 *     open trade this DR gates (dataRequest.service#chase), so this panel no
 *     longer does its own per-trade fan-out POSTs.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ageFromISO,
  answerDataRequest,
  chaseDataRequest,
  fetchDataRequests,
  fetchDrGating,
  type ChaseLogEntry,
  type DataRequestRow,
  type DrGatedInterface,
  tradeHref,
} from '../../api/interlockApi';
import { can } from '@fmeastudio/shared';
import { getActor, userById } from '../../utils/interlockActor';
import { announce } from './liveRegion';
import { useFocusTrap } from './useFocusTrap';
import './DrPanel.css';

/** Y5c — the honest cap on a chase note. Matches what the composer previously let
 * someone type past without warning, only to discover truncation (or a server
 * rejection) at submit time. */
const CHASE_NOTE_MAX_LENGTH = 2000;

function chaseSummaryLine(log: ChaseLogEntry[]): string {
  if (log.length === 0) return '';
  const last = log[log.length - 1];
  const who = userById(last.byUserId)?.short ?? last.byUserId ?? 'someone';
  return `chased ${log.length}× · last by ${who} ${ageFromISO(last.at)}`;
}

type LoadState = 'loading' | 'ready' | 'not-found' | 'error';

export function DrPanel({
  refId,
  onClose,
  onMutated,
}: {
  refId: string;
  onClose: () => void;
  /** G4 — fires after a mutation here (currently: answering the DR) that could
   * change agreement-state predicates elsewhere on the page hosting this panel
   * (e.g. GatePage's "no open DRs gating release" row). Optional: most hosts
   * don't have anything DR-dependent to recompute. */
  onMutated?: () => void;
}) {
  const navigate = useNavigate();
  // R25 (a11y BLOCKER) — the panel dimmed the background but trapped nothing:
  // no focus moved into it on open (activeElement stayed on <body>), Tab walked
  // the whole nav rail behind it before ever reaching the ✕/answer controls
  // (~39 presses), and a screen reader was never told a dialog opened. The shared
  // hook (already used by the inline trade/interface panels) moves focus in on
  // open, wraps Tab within the panel, handles Escape, and restores focus to the
  // trigger on close — so it replaces this component's old Escape-only listener.
  // Always active while mounted: InterlockScaffold mounts DrPanel only when open.
  const panelRef = useFocusTrap<HTMLDivElement>(true, onClose);
  const [state, setState] = useState<LoadState>('loading');
  const [dr, setDr] = useState<DataRequestRow | null>(null);
  const [gated, setGated] = useState<DrGatedInterface[]>([]);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  // Bug fix (#215 frontend half) — X7 originally hardcoded `role === 'ADMIN'`,
  // but the backend requires the `TRADE_PARTICIPATE` capability on
  // POST /data-requests/:id/answer (see interlockDataRequest.routes.ts), which
  // PM/ENGINEER/MANAGER/ADMIN all hold — VIEWER doesn't. Mirror the backend's
  // actual gate instead of a role the backend no longer checks, so PM/Engineer
  // get the composer they can already use via the API.
  const canParticipate = can(getActor().role, 'TRADE_PARTICIPATE');
  const [answerSummary, setAnswerSummary] = useState('');
  const [answerEvidence, setAnswerEvidence] = useState('');
  const [answering, setAnswering] = useState(false);
  const [toast, setToast] = useState<{ msg: string; error: boolean } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    setDr(null);
    setGated([]);
    setNote('');
    (async () => {
      try {
        const rows = await fetchDataRequests();
        const match = rows.find((r) => r.refId === refId);
        if (!match) {
          if (!cancelled) setState('not-found');
          return;
        }
        if (!cancelled) {
          setDr(match);
          setState('ready');
        }
        // Gating is a heavier fetch (N interfaces in parallel) — don't block the
        // header/ask text on it.
        const gatedRows = await fetchDrGating(refId);
        if (!cancelled) setGated(gatedRows);
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refId]);

  // R25 — Escape is now handled by useFocusTrap (panelRef); the old window-level
  // Escape listener here is removed to avoid a double onClose.

  const showToast = (msg: string, error = false) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, error });
    // A1 — same text into the shared polite live region as the visual toast.
    announce(error ? `Error: ${msg}` : msg);
    toastTimer.current = setTimeout(() => {
      if (mounted.current) setToast(null);
    }, error ? 6000 : 4000);
  };

  const openTrades = gated.flatMap((i) => i.openTrades);
  // De-dupe (an interface can theoretically appear once; trades are unique by id already).
  const uniqueOpenTrades = Array.from(new Map(openTrades.map((t) => [t.id, t])).values());

  const [historyOpen, setHistoryOpen] = useState(false);

  // V8 — always available: the composer no longer requires an open gated trade
  // to exist. The single POST both records the chase (persisted server-side in
  // `chaseLog`, so it survives a re-open of the panel — see the useEffect above,
  // which re-fetches from fetchDataRequests()) and, if this DR gates any open
  // trades, echoes the note into their logs (server-side, dataRequest.service#chase).
  const sendChaseNote = async () => {
    if (sending) return; // re-entrancy guard — this is the panel's only write
    const body = note.trim();
    if (!body || !dr) return;
    setSending(true);
    try {
      const updated = await chaseDataRequest(dr.id, body);
      setDr(updated);
      setNote('');
      showToast(
        uniqueOpenTrades.length > 0
          ? `Chase note logged · echoed to ${uniqueOpenTrades.length} open trade${uniqueOpenTrades.length === 1 ? '' : 's'}`
          : 'Chase note logged'
      );
    } catch (e) {
      // Y5c — surface the server's actual message (the way other Interlock panels
      // do, e.g. TradeDetailPage's runMutation) instead of a fixed generic string
      // that hides why the chase note actually failed.
      showToast(e instanceof Error ? e.message : 'Chase note failed to log', true);
    } finally {
      setSending(false);
    }
  };

  // X7 — POST /api/data-requests/:id/answer (interlockDataRequest.routes.ts:
  // `{ summary, evidenceRef? }`). On success re-render from the updated DR so the
  // ANSWER section above appears immediately, same panel instance.
  const sendAnswer = async () => {
    if (answering) return;
    const summary = answerSummary.trim();
    if (!summary || !dr) return;
    setAnswering(true);
    try {
      const updated = await answerDataRequest(dr.id, summary, answerEvidence.trim() || null);
      setDr(updated);
      setAnswerSummary('');
      setAnswerEvidence('');
      showToast(`${refId} answered`);
      // G4 — an answered DR can flip a gate's "no open DRs gating release" row;
      // let the host page know so it can refetch in place.
      onMutated?.();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to record answer', true);
    } finally {
      setAnswering(false);
    }
  };

  return (
    <>
      <div className="ilk-dr-scrim" onClick={onClose} />
      <div
        className="ilk-dr-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`${refId} data request detail`}
        ref={panelRef}
        // R25 — the focus-trap's Escape listener is container-level, so it only
        // fires while focus is inside the panel. Making the panel itself focusable
        // (-1: programmatic/click focus, not in the Tab order) means a click into
        // dead panel space (loading state, body text, gaps) keeps focus on the
        // container rather than falling to <body>, so Escape keeps working there.
        tabIndex={-1}
      >
        <div className="ilk-dr-head">
          <div className="ilk-dr-head-titlerow">
            <span className="ilk-dr-glyph">⬡</span>
            <h3 className="ilk-dr-title">{refId}</h3>
            {dr && <DrStatusChip status={dr.status} />}
          </div>
          <button type="button" className="ilk-dr-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="ilk-dr-body">
          {state === 'loading' && <div className="ilk-dr-empty">Loading {refId}…</div>}
          {state === 'error' && (
            <div className="ilk-dr-empty is-error">
              Couldn't reach the backend for {refId}. Try again shortly.
            </div>
          )}
          {state === 'not-found' && (
            <div className="ilk-dr-empty">no DR {refId} found</div>
          )}

          {state === 'ready' && dr && (
            <>
              <section className="ilk-dr-section">
                <div className="ilk-dr-sectag">ASK</div>
                <div className="ilk-dr-ask">{dr.ask}</div>
                <div className="ilk-dr-age">
                  asked {ageFromISO(dr.createdAt)}
                  {dr.answeredAt ? ` · answered ${ageFromISO(dr.answeredAt)}` : ''}
                </div>
              </section>

              {/* Z11: the answer is a fact about the DR, not the status enum — an
                  ANSWERED DR that later gets CLOSED (or any status carrying a
                  recorded answer) should still show it. Gate on the content
                  itself (answerSummary || answeredAt), not on `status ===
                  'ANSWERED'` specifically. */}
              {(dr.answerSummary || dr.answeredAt) && (
                <section className="ilk-dr-section">
                  <div className="ilk-dr-sectag">
                    {dr.status === 'CLOSED' ? 'ANSWERED · CLOSED' : 'ANSWERED'}
                  </div>
                  <div className="ilk-dr-answer">
                    {dr.answerSummary || <span className="ilk-dr-empty-mini">No summary recorded.</span>}
                  </div>
                  {dr.answeredByName && (
                    <div className="ilk-dr-answered-by">by {dr.answeredByName}</div>
                  )}
                  {dr.evidenceRef && (
                    <div className="ilk-dr-evidence">evidence: {dr.evidenceRef}</div>
                  )}
                </section>
              )}

              {dr.status === 'OPEN' && canParticipate && (
                <section className="ilk-dr-section ilk-dr-answerform">
                  <div className="ilk-dr-sectag">RECORD ANSWER</div>
                  <textarea
                    className="ilk-dr-chase-textarea"
                    placeholder={`Answer summary for ${refId}…`}
                    value={answerSummary}
                    onChange={(e) => setAnswerSummary(e.target.value)}
                    rows={3}
                  />
                  <input
                    className="ilk-dr-answer-evidence-input"
                    placeholder="Evidence reference (optional)"
                    value={answerEvidence}
                    onChange={(e) => setAnswerEvidence(e.target.value)}
                  />
                  <button
                    type="button"
                    className="ilk-dr-chase-btn"
                    disabled={answering || !answerSummary.trim()}
                    onClick={sendAnswer}
                  >
                    {answering ? 'Recording…' : 'Record answer'}
                  </button>
                </section>
              )}

              <section className="ilk-dr-section">
                <div className="ilk-dr-sectag">WHAT IT GATES</div>
                {gated.length === 0 ? (
                  <div className="ilk-dr-empty-mini">
                    gates nothing currently — informational.
                  </div>
                ) : (
                  <div className="ilk-dr-gatelist">
                    {gated.map((g) => (
                      <div key={g.refId} className="ilk-dr-gaterow">
                        <button
                          type="button"
                          className="ilk-dr-ifchip"
                          onClick={() => {
                            onClose();
                            navigate(`/interlock/interfaces/${g.refId}`);
                          }}
                          title={g.label}
                        >
                          {g.refId}
                        </button>
                        <span className="ilk-dr-gatelabel">{g.label}</span>
                        <div className="ilk-dr-tradechips">
                          {g.openTrades.length === 0 ? (
                            <span className="ilk-dr-empty-mini">no open trades</span>
                          ) : (
                            g.openTrades.map((t) => (
                              <button
                                key={t.id}
                                type="button"
                                className="ilk-dr-tradechip"
                                onClick={() => {
                                  onClose();
                                  navigate(tradeHref(t.refId));
                                }}
                                title={t.title}
                              >
                                ◆ {t.refId}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="ilk-dr-section ilk-dr-chase">
                <div className="ilk-dr-sectag">CHASE</div>
                {dr.chaseLog.length > 0 && (
                  <div className="ilk-dr-chase-history">
                    <button
                      type="button"
                      className="ilk-dr-chase-summary"
                      onClick={() => setHistoryOpen((v) => !v)}
                      aria-expanded={historyOpen}
                    >
                      {chaseSummaryLine(dr.chaseLog)}
                      <span className="ilk-dr-chase-summary-chev">{historyOpen ? '▾' : '▸'}</span>
                    </button>
                    {historyOpen && (
                      <ul className="ilk-dr-chase-list">
                        {[...dr.chaseLog].reverse().map((c, i) => {
                          const who = userById(c.byUserId)?.short ?? c.byUserId ?? 'someone';
                          return (
                            <li key={i} className="ilk-dr-chase-entry">
                              <span className="ilk-dr-chase-entry-head">
                                <strong>{who}</strong> · {ageFromISO(c.at)}
                              </span>
                              <span className="ilk-dr-chase-entry-note">{c.note}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
                {/* V8 — composer is ALWAYS available; it no longer requires an
                    open gated trade. If there ARE open gated trades, the hint
                    says so (the note also echoes there); otherwise it's just
                    logged on the DR itself. */}
                <div className="ilk-dr-chase-hint">
                  {uniqueOpenTrades.length > 0
                    ? `Also echoes to ${uniqueOpenTrades.length} open trade${uniqueOpenTrades.length === 1 ? '' : 's'}: ${uniqueOpenTrades.map((t) => t.refId).join(', ')}`
                    : `No open trades gated by ${refId} right now — this note is logged on the DR only.`}
                </div>
                {/* Bug fix (inverse gap to X7) — POST /data-requests/:id/chase is
                    also gated on TRADE_PARTICIPATE (interlockDataRequest.routes.ts),
                    but this composer had zero UI gating: a VIEWER saw a fully
                    enabled textarea + button and only learned it was forbidden
                    after a 403 on submit. Mirror RaiseDrControl's disable-with-
                    tooltip pattern (InterfaceDetailPage.tsx) — stays discoverable,
                    but can't be driven into a request that will 403. */}
                <textarea
                  className="ilk-dr-chase-textarea"
                  placeholder={
                    canParticipate
                      ? `Chase note for ${refId}…`
                      : 'You need Trade Participate access to log a chase note.'
                  }
                  value={note}
                  disabled={!canParticipate}
                  title={
                    canParticipate
                      ? undefined
                      : 'You need Trade Participate access to log a chase note.'
                  }
                  onChange={(e) => setNote(e.target.value.slice(0, CHASE_NOTE_MAX_LENGTH))}
                  maxLength={CHASE_NOTE_MAX_LENGTH}
                  rows={3}
                />
                {/* Y5c — the cap needs to be visible, not just enforced silently:
                    a live counter that turns amber near the limit so a long note
                    doesn't discover the cap only when it gets truncated on submit. */}
                <div
                  className={`ilk-dr-chase-counter${
                    note.length >= CHASE_NOTE_MAX_LENGTH
                      ? ' is-at-limit'
                      : note.length >= CHASE_NOTE_MAX_LENGTH * 0.9
                        ? ' is-near-limit'
                        : ''
                  }`}
                >
                  {note.length}/{CHASE_NOTE_MAX_LENGTH}
                </div>
                <button
                  type="button"
                  className="ilk-dr-chase-btn"
                  disabled={!canParticipate || sending || !note.trim()}
                  title={
                    canParticipate
                      ? undefined
                      : 'You need Trade Participate access to log a chase note.'
                  }
                  onClick={sendChaseNote}
                >
                  {sending
                    ? 'Logging…'
                    : uniqueOpenTrades.length > 0
                      ? `Log chase note → ${uniqueOpenTrades.length} trade${uniqueOpenTrades.length === 1 ? '' : 's'}`
                      : 'Log chase note'}
                </button>
              </section>
            </>
          )}
        </div>

        {toast && <div className={`ilk-dr-toast${toast.error ? ' is-error' : ''}`}>{toast.msg}</div>}
      </div>
    </>
  );
}

function DrStatusChip({ status }: { status: DataRequestRow['status'] }) {
  return <span className={`ilk-dr-statuschip st-${status.toLowerCase()}`}>{status}</span>;
}
